import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const session = await apiFetch("/api/auth/me", { notifyOnUnauthorized: false });
            setUser(session);
        } catch (error) {
            if (error.status === 401) {
                setUser(null);
            } else {
                throw error;
            }
        }
    }, []);

    useEffect(() => {
        refresh()
            .catch(() => setUser(null))
            .finally(() => setLoading(false));
    }, [refresh]);

    useEffect(() => {
        const handleUnauthorized = () => setUser(null);
        window.addEventListener("saiconnect:unauthorized", handleUnauthorized);
        return () => window.removeEventListener("saiconnect:unauthorized", handleUnauthorized);
    }, []);

    const login = useCallback(async (accessCode) => {
        const session = await apiFetch("/api/auth/login", {
            method: "POST",
            notifyOnUnauthorized: false,
            body: JSON.stringify({ accessCode }),
        });
        setUser(session);
        return session;
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiFetch("/api/auth/logout", { method: "POST", notifyOnUnauthorized: false });
        } catch (_error) {
            // Local authentication state is cleared even if the server is unavailable.
        } finally {
            setUser(null);
        }
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, logout, refresh }),
        [user, loading, login, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within AuthProvider");
    return context;
};
